<template>
    <div>
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-medium text-gray-700 dark:text-gray-200">{{ title }}</h3>
        <div class="flex space-x-2">
          <button
            v-for="(period, index) in timePeriods"
            :key="index"
            @click="selectedPeriod = period.value"
            class="px-2 py-1 text-xs rounded-md transition-colors"
            :class="selectedPeriod === period.value 
              ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-200' 
              : 'bg-white text-gray-600 dark:bg-gray-800 dark:text-gray-300'"
          >
            {{ period.label }}
          </button>
        </div>
      </div>
      
      <div class="relative h-64">
        <!-- Placeholder for chart - in a real implementation, you would use a library like Chart.js or recharts -->
        <div v-if="loading" class="absolute inset-0 flex items-center justify-center">
          <svg class="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        
        <div v-else class="w-full h-full">
          <!-- Simulated chart with line -->
          <div class="relative w-full h-full flex items-end">
            <!-- Y axis labels -->
            <div class="absolute inset-y-0 left-0 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 py-4">
              <span>100</span>
              <span>75</span>
              <span>50</span>
              <span>25</span>
              <span>0</span>
            </div>
            
            <!-- Chart content -->
            <div class="ml-8 w-full h-full border-l border-b dark:border-gray-700 relative">
              <!-- Grid lines -->
              <div class="absolute inset-0 grid grid-cols-1 grid-rows-4">
                <div class="border-t dark:border-gray-700"></div>
                <div class="border-t dark:border-gray-700"></div>
                <div class="border-t dark:border-gray-700"></div>
                <div class="border-t dark:border-gray-700"></div>
              </div>
              
              <!-- Simulated data line -->
              <svg class="absolute inset-0" preserveAspectRatio="none" viewBox="0 0 100 100">
                <!-- NPS Score line -->
                <path
                  d="M0,30 L10,40 L20,35 L30,25 L40,20 L50,30 L60,25 L70,20 L80,15 L90,25 L100,20"
                  stroke="#3B82F6"
                  stroke-width="2"
                  fill="none"
                />
                
                <!-- Promoters area -->
                <path
                  d="M0,65 L10,60 L20,55 L30,50 L40,45 L50,40 L60,35 L70,30 L80,25 L90,20 L100,25 L100,100 L0,100 Z"
                  fill="#10B981"
                  fill-opacity="0.1"
                />
                
                <!-- Detractors area -->
                <path
                  d="M0,80 L10,85 L20,82 L30,78 L40,80 L50,76 L60,80 L70,85 L80,82 L90,85 L100,80 L100,100 L0,100 Z"
                  fill="#EF4444"
                  fill-opacity="0.1"
                />
              </svg>
              
              <!-- X axis labels -->
              <div class="absolute left-0 right-0 bottom-0 flex justify-between text-xs text-gray-500 dark:text-gray-400 translate-y-6 px-2">
                <span v-for="month in chartLabels" :key="month">{{ month }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Legend -->
      <div class="flex space-x-4 mt-6 justify-center text-sm">
        <div class="flex items-center">
          <span class="inline-block w-3 h-3 bg-blue-500 rounded-full mr-1"></span>
          <span class="text-gray-600 dark:text-gray-300">NPS Score</span>
        </div>
        <div class="flex items-center">
          <span class="inline-block w-3 h-3 bg-green-500 opacity-40 rounded-full mr-1"></span>
          <span class="text-gray-600 dark:text-gray-300">Promoters</span>
        </div>
        <div class="flex items-center">
          <span class="inline-block w-3 h-3 bg-red-500 opacity-40 rounded-full mr-1"></span>
          <span class="text-gray-600 dark:text-gray-300">Detractors</span>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, computed, watch, onMounted } from 'vue';
  
  export default {
    name: 'NpsChart',
    props: {
      title: {
        type: String,
        default: 'NPS Score Trend'
      },
      companyId: {
        type: [String, Number],
        default: null
      }
    },
    setup(props) {
      const loading = ref(true);
      const chartData = ref([]);
      
      const timePeriods = [
        { label: '7 dias', value: '7d' },
        { label: '30 dias', value: '30d' },
        { label: '90 dias', value: '90d' },
        { label: '1 ano', value: '1y' }
      ];
      
      const selectedPeriod = ref('30d');
      
      // These would come from API in a real implementation
      const chartLabels = computed(() => {
        if (selectedPeriod.value === '7d') {
          return ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
        } else if (selectedPeriod.value === '30d') {
          return ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4'];
        } else if (selectedPeriod.value === '90d') {
          return ['Jan', 'Fev', 'Mar'];
        } else {
          return ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        }
      });
      
      // Simulate data loading
      const loadChartData = async () => {
        loading.value = true;
        
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Simulated data
        chartData.value = [
          { period: 'Jan', nps: 72, promoters: 75, detractors: 10 },
          { period: 'Fev', nps: 68, promoters: 70, detractors: 12 },
          { period: 'Mar', nps: 75, promoters: 78, detractors: 8 },
          { period: 'Abr', nps: 80, promoters: 82, detractors: 5 },
          // More data would be here
        ];
        
        loading.value = false;
      };
      
      // Watch for period changes to reload data
      watch(selectedPeriod, () => {
        loadChartData();
      });
      
      // Initial load
      onMounted(() => {
        loadChartData();
      });
      
      return {
        loading,
        chartData,
        timePeriods,
        selectedPeriod,
        chartLabels
      };
    }
  };
  </script>